import { Link, useLocation } from '@remix-run/react';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';

import { POST_AUTH_LANDING_PATH } from '~/config/landing';
import { clearOfficeStandaloneNav } from '~/utils/office-nav';

export interface SidebarProps {
  companyName?: string;
  companyLogo?: string;
  mainOfficeId?: string;
  unreadNotificationCount?: number;
  newLeadsCount?: number;
  /** Hide the Contact Us Leads tab until the company has a Contact Us web form deployed (or has leads). */
  showLeadsNav?: boolean;
  canManageTeamProfiles?: boolean;
  canManageBilling?: boolean;
  isSuperAdmin?: boolean;
}

const itemClass = (active: boolean) =>
  `flex items-center space-x-3 px-4 py-3 rounded-lg ${
    active
      ? 'text-primary font-medium bg-gradient-to-r from-zen-300 to-zen-light'
      : 'text-primary hover:bg-gradient-to-r hover:from-zen-300 hover:to-zen-light'
  }`;

// Icons copied verbatim from AuthenticatedLayout.tsx (line ranges of the
// current file, before Task 2 removes them):
const ICONS = {
  // lines 184-191
  analytics: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
      />
    </svg>
  ),
  // lines 206-213
  referrals: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
      />
    </svg>
  ),
  // lines 241-253
  incoming: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M7.5 10.5 12 15m0 0 4.5-4.5M12 15V3"
      />
    </svg>
  ),
  // lines 265-272
  outgoing: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 8.25V6A2.25 2.25 0 015.25 3.75h13.5A2.25 2.25 0 0121 6v2.25M7.5 12 12 7.5m0 0 4.5 4.5M12 7.5V21"
      />
    </svg>
  ),
  completed: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  ),
  leads: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17.982 18.725A7.488 7.488 0 0012 15.75a7.488 7.488 0 00-5.982 2.975m11.964 0a9 9 0 10-11.964 0m11.964 0A8.966 8.966 0 0112 21a8.966 8.966 0 01-5.982-2.275M15 9.75a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  ),
  // lines 288-295
  notifications: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
      />
    </svg>
  ),
  // lines 509-511
  notes: (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M22 10H6c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2v-8c0-1.1-.9-2-2-2zm-6 8H8v-2h8v2zm4-4H8v-2h12v2zM14 2H2c-1.1 0-2 .9-2 2v14h2V4h12V2z" />
    </svg>
  ),
  // lines 486-497
  company: (
    <svg
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
    >
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <path d="M9 3v18M15 3v18M3 9h18M3 15h18" />
    </svg>
  ),
  // lines 323-335
  offices: (
    <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
      />
    </svg>
  ),
  // lines 369-381
  embedForm: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
      />
    </svg>
  ),
  // lines 392-404
  referralAccess: (
    <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
      />
    </svg>
  ),
  // lines 415-427
  officeDetails: (
    <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  ),
  // lines 438-451
  teamRoles: (
    <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
      />
    </svg>
  ),
  // lines 466-473
  team: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
      />
    </svg>
  ),
  // lines 522-535
  memberships: (
    <svg
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
    >
      <rect x="3" y="4" width="18" height="16" rx="2" ry="2" />
      <path d="M3 10h18" />
      <path d="M8 14h2" />
      <path d="M14 14h2" />
    </svg>
  ),
  // lines 548-555
  billing: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
      />
    </svg>
  ),
  // lines 568-581
  mySettings: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  ),
  // lines 607-620
  admin: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  ),
};

type Gate =
  | 'canManageTeamProfiles'
  | 'canManageBilling'
  | 'isSuperAdmin'
  | 'showLeadsNav'
  | 'hasMainOffice';

type NavEntry =
  | {
      type: 'link';
      label: string;
      to: string;
      icon: ReactNode;
      gate?: Gate;
      demoTour?: string;
      badgeKey?: 'notifications' | 'leads';
      activeTest?: (pathname: string) => boolean;
    }
  | { type: 'referrals' }
  | { type: 'offices' }
  | { type: 'settings' };

const SECTIONS: NavEntry[][] = [
  [
    {
      type: 'link',
      label: 'Analytics',
      to: '/analytics',
      icon: ICONS.analytics,
      demoTour: 'nav-analytics',
    },
    { type: 'referrals' },
    {
      type: 'link',
      label: 'Contact Us Leads',
      to: '/leads',
      icon: ICONS.leads,
      badgeKey: 'leads',
      gate: 'showLeadsNav',
    },
    {
      type: 'link',
      label: 'Notifications',
      to: '/notifications',
      icon: ICONS.notifications,
      badgeKey: 'notifications',
    },
    { type: 'link', label: 'Notes', to: '/notes', icon: ICONS.notes },
    {
      type: 'link',
      label: 'Website Lead Forms',
      to: '/company-settings/embed',
      icon: ICONS.embedForm,
      gate: 'hasMainOffice',
      activeTest: pathname =>
        pathname === '/company-settings/embed' || /^\/offices\/[^/]+\/embed(\/|$)/.test(pathname),
    },
  ],
  [{ type: 'settings' }, { type: 'offices' }],
  [{ type: 'link', label: 'Admin', to: '/ops/console', icon: ICONS.admin, gate: 'isSuperAdmin' }],
];

export function Sidebar(props: SidebarProps) {
  const {
    companyName,
    companyLogo,
    mainOfficeId = '',
    unreadNotificationCount = 0,
    newLeadsCount = 0,
    showLeadsNav = true,
    canManageTeamProfiles = false,
    canManageBilling = false,
    isSuperAdmin = false,
  } = props;
  const location = useLocation();
  const [isReferralsOpen, setIsReferralsOpen] = useState(true);
  const [isOfficesOpen, setIsOfficesOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  // Auto-open Settings submenu when the user is on a Settings sub-page so the
  // active child is visible, even on a cold load or a direct URL.
  const settingsSubPathActive =
    (location.pathname.startsWith('/company-settings') &&
      location.pathname !== '/company-settings/embed') ||
    location.pathname.startsWith('/membership-plans') ||
    location.pathname.startsWith('/billing') ||
    location.pathname.startsWith('/subscription') ||
    location.pathname === '/team';
  useEffect(() => {
    if (settingsSubPathActive) setIsSettingsOpen(true);
  }, [settingsSubPathActive]);
  const badgeCounts: Record<
    NonNullable<Extract<NavEntry, { type: 'link' }>['badgeKey']>,
    number
  > = {
    notifications: unreadNotificationCount,
    leads: newLeadsCount,
  };

  const gates: Record<Gate, boolean> = {
    canManageTeamProfiles,
    canManageBilling,
    isSuperAdmin,
    showLeadsNav,
    hasMainOffice: !!mainOfficeId,
  };

  // Office submenu active-state logic moved verbatim from AuthenticatedLayout:
  const officePathParts = location.pathname.split('/');
  const isEmbedPage = /^\/offices\/[^/]+\/embed(\/|$)/.test(location.pathname);
  const isMembershipsPage = location.pathname.startsWith('/membership-plans');
  const isBillingPage =
    location.pathname.startsWith('/billing') || location.pathname.startsWith('/subscription');
  // Office sub-pages that now live under Settings should NOT highlight Offices.
  const isOnOfficeRoute = location.pathname.startsWith('/offices') && !isEmbedPage;
  const officeRouteId = officePathParts[2] ?? '';
  const officeSubRoute = officePathParts[3] ?? '';
  const isOfficeDetailsActive =
    isOnOfficeRoute &&
    !!mainOfficeId &&
    officeRouteId === mainOfficeId &&
    (!officeSubRoute || officeSubRoute === '');
  const isRolesPage = location.pathname === '/team';
  const referralType = location.pathname.split('/')[2];

  // Moved verbatim from AuthenticatedLayout.tsx lines 195-277, with only the
  // state variable renamed to isReferralsOpen (already the case upstream) and
  // the state coming from Sidebar scope.
  //
  // Rendered via a plain function call (not as a JSX component element) so
  // its identity doesn't change across Sidebar re-renders — using it as
  // <ReferralsBlock /> would give React a new component type every render,
  // forcing a full unmount/remount of the subtree (chevron animation, focus,
  // demo-tour node stability all broken by that).
  function renderReferralsBlock() {
    const isReferralsActive = location.pathname.startsWith('/referrals');
    return (
      <div key="referrals" className="space-y-1">
        <div
          className={`flex items-center rounded-lg ${
            isReferralsActive
              ? 'text-primary font-medium bg-gradient-to-r from-zen-300 to-zen-light'
              : 'text-primary hover:bg-gradient-to-r hover:from-zen-300 hover:to-zen-light'
          }`}
        >
          <Link
            to="/referrals/incoming"
            data-demo-tour="nav-referrals"
            onClick={() => setIsReferralsOpen(true)}
            className="flex-1 flex items-center space-x-3 px-4 py-3"
          >
            {ICONS.referrals}
            <span>Referrals</span>
          </Link>
          <button
            type="button"
            aria-label={isReferralsOpen ? 'Collapse Referrals menu' : 'Expand Referrals menu'}
            aria-expanded={isReferralsOpen}
            onClick={() => setIsReferralsOpen(prev => !prev)}
            className="shrink-0 px-3 py-3 rounded-lg hover:bg-zen-300/40"
          >
            <svg
              className={`w-4 h-4 transition-transform ${isReferralsOpen ? 'rotate-180' : ''}`}
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
        {isReferralsOpen && (
          <div className="ml-6 space-y-1">
            <div>
              <Link
                to="/referrals/incoming"
                data-demo-tour="nav-referrals-incoming"
                className={`flex items-center space-x-3 px-4 py-2 rounded-lg ${
                  referralType === 'incoming'
                    ? 'text-primary font-medium bg-gradient-to-r from-zen-300 to-zen-light'
                    : 'text-primary hover:bg-gradient-to-r hover:from-zen-300 hover:to-zen-light'
                }`}
              >
                {ICONS.incoming}
                <span>Incoming</span>
              </Link>
            </div>
            <Link
              to="/referrals/outgoing"
              className={`flex items-center space-x-3 px-4 py-2 rounded-lg ${
                referralType === 'outgoing'
                  ? 'text-primary font-medium bg-gradient-to-r from-zen-300 to-zen-light'
                  : 'text-primary hover:bg-gradient-to-r hover:from-zen-300 hover:to-zen-light'
              }`}
            >
              {ICONS.outgoing}
              <span>Outgoing</span>
            </Link>
            <Link
              to="/referrals/completed"
              className={`flex items-center space-x-3 px-4 py-2 rounded-lg ${
                referralType === 'completed'
                  ? 'text-primary font-medium bg-gradient-to-r from-zen-300 to-zen-light'
                  : 'text-primary hover:bg-gradient-to-r hover:from-zen-300 hover:to-zen-light'
              }`}
            >
              {ICONS.completed}
              <span>Cases Completed</span>
            </Link>
          </div>
        )}
      </div>
    );
  }

  // Moved verbatim from AuthenticatedLayout.tsx lines 309-455, with the label/aria
  // renames noted in the task brief and isOfficeManagementOpen -> isOfficesOpen.
  //
  // Rendered via a plain function call, not a JSX component element — see the
  // comment on renderReferralsBlock above for why.
  function renderOfficesBlock() {
    return (
      <div key="offices" className="space-y-1">
        <div
          className={`flex items-center rounded-lg ${
            isOnOfficeRoute
              ? 'text-primary font-medium bg-gradient-to-r from-zen-300 to-zen-light'
              : 'text-primary hover:bg-gradient-to-r hover:from-zen-300 hover:to-zen-light'
          }`}
        >
          <Link
            to="/offices"
            data-demo-tour="nav-offices"
            onClick={() => clearOfficeStandaloneNav()}
            className="flex min-w-0 flex-1 items-center space-x-3 px-4 py-3 rounded-lg"
          >
            {ICONS.offices}
            <span>Offices</span>
          </Link>
          {mainOfficeId ? (
            <button
              type="button"
              onClick={() => setIsOfficesOpen(prev => !prev)}
              aria-expanded={isOfficesOpen}
              aria-controls="sidebar-offices-submenu"
              aria-label="Toggle Offices submenu"
              className="shrink-0 px-3 py-3 rounded-lg hover:bg-zen-300/40"
            >
              <svg
                className={`w-4 h-4 transition-transform ${isOfficesOpen ? 'rotate-180' : ''}`}
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          ) : null}
        </div>
        {isOfficesOpen && mainOfficeId ? (
          <div id="sidebar-offices-submenu" className="ml-6 space-y-1">
            <Link
              to={`/offices/${mainOfficeId}?standalone=1`}
              className={`flex items-center space-x-3 px-4 py-2 rounded-lg ${
                isOfficeDetailsActive
                  ? 'text-primary font-medium bg-gradient-to-r from-zen-300 to-zen-light'
                  : 'text-primary hover:bg-gradient-to-r hover:from-zen-300 hover:to-zen-light'
              }`}
            >
              {ICONS.officeDetails}
              <span>Details</span>
            </Link>
          </div>
        ) : null}
      </div>
    );
  }

  function renderSettingsBlock() {
    const subLinks: {
      to: string;
      label: string;
      isActive: boolean;
      gate?: Gate;
    }[] = [
      {
        to: '/company-settings',
        label: 'Company Settings',
        isActive: location.pathname === '/company-settings',
      },
      {
        to: '/team',
        label: 'Dental Team',
        gate: 'canManageTeamProfiles',
        isActive: isRolesPage,
      },
      {
        to: '/company-settings/referral-access',
        label: 'Referral Access Code',
        isActive: location.pathname.startsWith('/company-settings/referral-access'),
      },
      {
        to: '/company-settings/memberships',
        label: 'Memberships',
        isActive:
          isMembershipsPage || location.pathname.startsWith('/company-settings/memberships'),
      },
      {
        to: '/company-settings/billing',
        label: 'Billing',
        gate: 'canManageBilling',
        isActive: isBillingPage || location.pathname.startsWith('/company-settings/billing'),
      },
      {
        to: '/company-settings/my-settings',
        label: 'My Settings',
        isActive: location.pathname === '/company-settings/my-settings',
      },
    ];
    const visibleSubLinks = subLinks.filter(link => !link.gate || gates[link.gate]);
    const isSettingsActive = visibleSubLinks.some(link => link.isActive);
    return (
      <div key="settings" className="space-y-1">
        <div
          className={`flex items-center rounded-lg ${
            isSettingsActive
              ? 'text-primary font-medium bg-gradient-to-r from-zen-300 to-zen-light'
              : 'text-primary hover:bg-gradient-to-r hover:from-zen-300 hover:to-zen-light'
          }`}
        >
          <Link
            to="/company-settings"
            onClick={() => setIsSettingsOpen(true)}
            className="flex-1 flex items-center space-x-3 px-4 py-3"
          >
            {ICONS.mySettings}
            <span>Settings</span>
          </Link>
          <button
            type="button"
            aria-label={isSettingsOpen ? 'Collapse Settings menu' : 'Expand Settings menu'}
            aria-expanded={isSettingsOpen}
            onClick={() => setIsSettingsOpen(prev => !prev)}
            className="shrink-0 px-3 py-3 rounded-lg hover:bg-zen-300/40"
          >
            <svg
              className={`w-4 h-4 transition-transform ${isSettingsOpen ? 'rotate-180' : ''}`}
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
        {isSettingsOpen && (
          <div className="ml-6 space-y-1">
            {visibleSubLinks.map(link => (
              <Link
                key={link.to}
                to={link.to}
                className={`block px-4 py-2 rounded-lg ${
                  link.isActive
                    ? 'text-primary font-medium bg-gradient-to-r from-zen-300 to-zen-light'
                    : 'text-primary hover:bg-gradient-to-r hover:from-zen-300 hover:to-zen-light'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  }

  const visibleSections = SECTIONS.map(section =>
    section.filter(entry => entry.type !== 'link' || !entry.gate || gates[entry.gate])
  ).filter(section => section.length > 0);

  return (
    <aside className="w-64 shrink-0 bg-zen-light text-primary">
      {/* Logo block copied verbatim from AuthenticatedLayout.tsx lines 165-172 */}
      <div className="p-6 flex flex-col items-center">
        <Link to={POST_AUTH_LANDING_PATH} className="flex flex-col items-center space-y-2">
          {companyName && (
            <h2 className="text-lg font-semibold text-primary text-center">{companyName}</h2>
          )}
          <img src={companyLogo || '/logo.svg'} alt="ToothReferral" className="max-h-[6rem]" />
        </Link>
      </div>

      <nav className="mt-6 px-4" aria-label="Sidebar navigation">
        {visibleSections.map((section, sectionIndex) => (
          <div key={sectionIndex}>
            {sectionIndex > 0 && (
              <div
                data-testid="sidebar-divider"
                aria-hidden="true"
                className="my-3 border-t border-zen-300"
              />
            )}
            {section.map(entry => {
              if (entry.type === 'referrals') return renderReferralsBlock();
              if (entry.type === 'offices') return renderOfficesBlock();
              if (entry.type === 'settings') return renderSettingsBlock();
              const isActive = entry.activeTest
                ? entry.activeTest(location.pathname)
                : location.pathname.startsWith(entry.to);
              return (
                <Link
                  key={entry.to}
                  to={entry.to}
                  data-demo-tour={entry.demoTour}
                  className={itemClass(isActive)}
                >
                  {entry.badgeKey ? (
                    // Badge wrapper copied verbatim from AuthenticatedLayout.tsx lines 287-305
                    <span className="relative inline-flex">
                      {entry.icon}
                      {badgeCounts[entry.badgeKey] > 0 && (
                        <span
                          data-testid={
                            entry.badgeKey === 'notifications'
                              ? 'sidebar-unread-badge'
                              : 'sidebar-new-leads-badge'
                          }
                          className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white"
                          aria-label={
                            entry.badgeKey === 'notifications'
                              ? `${badgeCounts[entry.badgeKey]} unread notifications`
                              : `${badgeCounts[entry.badgeKey]} new leads`
                          }
                        >
                          {badgeCounts[entry.badgeKey] > 99 ? '99+' : badgeCounts[entry.badgeKey]}
                        </span>
                      )}
                    </span>
                  ) : (
                    entry.icon
                  )}
                  <span>{entry.label}</span>
                </Link>
              );
            })}
          </div>
        ))}
      </nav>
    </aside>
  );
}
